'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const LibraryPage = () => {
    const [searchQuery, setSearchQuery] = useState('');

    // Sample bookshelves with resources - each with unique color theme
    const bookshelves = [
        {
            id: 'getting-started',
            title: 'Getting Started',
            icon: 'RocketLaunchIcon',
            color: 'from-blue-500 to-cyan-500',
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200',
            books: [
                { title: 'Urban Farming Basics', author: 'MicroSpace Team', pages: 24, color: 'bg-blue-100' },
                { title: 'Space Planning 101', author: 'MicroSpace Team', pages: 18, color: 'bg-blue-100' },
                { title: 'First Time Gardener Guide', author: 'Community', pages: 32, color: 'bg-blue-100' },
            ]
        },
        {
            id: 'recipes',
            title: 'Recipes & Cooking',
            icon: 'SparklesIcon',
            color: 'from-orange-500 to-amber-500',
            bgColor: 'bg-orange-50',
            borderColor: 'border-orange-200',
            books: [
                { title: 'Healthy & Delicious Garlic Recipes You’ll Love', author: 'MicroSpace Kitchen', pages: 42, color: 'bg-orange-100', image: '/imgs/books/Garlic-recipes-you-will-love.png', href: '/library/books/garlic-recipes/introduction' },
                { title: 'Fresh Herb Recipes', author: 'Kitchen Garden', pages: 52, color: 'bg-orange-100' },
                { title: 'Microgreens in Cuisine', author: 'Chef\'s Table', pages: 41, color: 'bg-orange-100' },
                { title: 'Preserving Your Harvest', author: 'Food Storage', pages: 48, color: 'bg-orange-100' },
                { title: 'Farm-to-Table Meals', author: 'Urban Cook', pages: 63, color: 'bg-orange-100' },
            ]
        },
        {
            id: 'advanced-techniques',
            title: 'Advanced Techniques',
            icon: 'BeakerIcon',
            color: 'from-purple-500 to-pink-500',
            bgColor: 'bg-purple-50',
            borderColor: 'border-purple-200',
            books: [
                { title: 'Hydroponic Systems', author: 'Expert Series', pages: 45, color: 'bg-purple-100' },
                { title: 'Vertical Farming Mastery', author: 'Pro Growers', pages: 56, color: 'bg-purple-100' },
                { title: 'Season Extension Methods', author: 'Climate Guide', pages: 38, color: 'bg-purple-100' },
            ]
        },
        {
            id: 'plant-guides',
            title: 'Plant Guides',
            icon: 'SparklesIcon',
            color: 'from-green-500 to-emerald-500',
            bgColor: 'bg-green-50',
            borderColor: 'border-green-200',
            books: [
                { title: 'Herbs Encyclopedia', author: 'Plant Library', pages: 67, color: 'bg-green-100' },
                { title: 'Microgreens Handbook', author: 'Quick Crops', pages: 29, color: 'bg-green-100' },
                { title: 'Container Vegetables', author: 'Urban Harvest', pages: 51, color: 'bg-green-100' },
            ]
        },
        {
            id: 'troubleshooting',
            title: 'Troubleshooting & Tips',
            icon: 'WrenchScrewdriverIcon',
            color: 'from-red-500 to-rose-500',
            bgColor: 'bg-red-50',
            borderColor: 'border-red-200',
            books: [
                { title: 'Common Pests & Solutions', author: 'Problem Solvers', pages: 42, color: 'bg-red-100' },
                { title: 'Nutrient Deficiency Guide', author: 'Plant Health', pages: 28, color: 'bg-red-100' },
                { title: 'Climate Challenges', author: 'Adaptation Guide', pages: 35, color: 'bg-red-100' },
            ]
        },
    ];

    // Filter bookshelves based on search query
    const filteredBookshelves = bookshelves
        .map((shelf) => ({
            ...shelf,
            books: shelf.books.filter(
                (book) =>
                    book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    book.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    shelf.title.toLowerCase().includes(searchQuery.toLowerCase())
            ),
        }))
        .filter((shelf) => shelf.books.length > 0);

    const hasResults = filteredBookshelves.length > 0;

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center mb-4">
                        <Icon name="BookOpenIcon" size={48} className="text-primary" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-extrabold text-foreground mb-4 font-heading">
                        Resource Library
                    </h1>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                        Explore our curated collection of guides, tutorials, and resources to help you succeed in urban farming and space optimization.
                    </p>
                </div>

                {/* Search Bar */}
                <div className="max-w-2xl mx-auto mb-12">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Icon name="MagnifyingGlassIcon" size={20} className="text-muted-foreground" />
                        </div>
                        <input
                            type="text"
                            id="library-search"
                            name="library-search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search books by title, author, or category..."
                            className="w-full pl-12 pr-12 py-4 bg-card border-2 border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder-muted-foreground transition-all"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                                aria-label="Clear search"
                            >
                                <Icon name="XMarkIcon" size={20} />
                            </button>
                        )}
                    </div>
                    {searchQuery && (
                        <p className="mt-2 text-sm text-muted-foreground text-center">
                            Searching for: <span className="font-semibold text-foreground">"{searchQuery}"</span>
                        </p>
                    )}
                </div>

                {/* Bookshelves Grid */}
                {hasResults ? (
                    <div className="space-y-12">
                        {filteredBookshelves.map((shelf) => (
                            <div key={shelf.id} className={`${shelf.bgColor} rounded-lg border-2 ${shelf.borderColor} p-6 shadow-lg`}>
                                {/* Shelf Header with Gradient */}
                                <div className={`flex items-center mb-6 pb-4 px-4 -mx-6 -mt-6 pt-6 rounded-t-lg bg-gradient-to-r ${shelf.color}`}>
                                    <Icon name={shelf.icon as any} size={32} className="text-white mr-3 drop-shadow-md" />
                                    <h2 className="text-2xl font-bold text-white font-heading drop-shadow-md">
                                        {shelf.title}
                                    </h2>
                                </div>

                                {/* Books Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                                    {shelf.books.map((book: any, index) => {
                                        const CardContent = (
                                            <div
                                                className={`group ${book.color} hover:shadow-xl rounded-lg p-5 transition-all duration-200 cursor-pointer border-2 border-transparent hover:border-primary/50 hover:scale-105 flex flex-col h-full`}
                                            >
                                                <div className="flex items-start flex-1">
                                                    {book.image ? (
                                                        <div className="flex-shrink-0 w-24 h-32 mr-4 rounded-md overflow-hidden shadow-sm group-hover:shadow-md transition-all">
                                                            <img src={book.image} alt={book.title} className="w-full h-full object-cover" />
                                                        </div>
                                                    ) : (
                                                        <div className="flex-shrink-0 w-12 h-16 bg-gradient-to-br from-primary/30 to-primary/10 rounded flex items-center justify-center mr-4 group-hover:from-primary/40 group-hover:to-primary/20 transition-all shadow-sm">
                                                            <Icon name="DocumentTextIcon" size={24} className="text-primary" />
                                                        </div>
                                                    )}

                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-base font-semibold text-foreground mb-1 group-hover:text-primary transition-colors line-clamp-2">
                                                            {book.title}
                                                        </h3>
                                                        <p className="text-sm text-muted-foreground mb-2">{book.author}</p>
                                                        <div className="flex items-center text-xs text-muted-foreground">
                                                            <Icon name="DocumentIcon" size={14} className="mr-1" />
                                                            <span>{book.pages} pages</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Coming Soon Badge - Only show if NO href */}
                                                {!book.href && (
                                                    <div className="mt-3 pt-3 border-t border-border/50">
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                                            <Icon name="ClockIcon" size={12} className="mr-1" />
                                                            Coming Soon
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        );

                                        return book.href ? (
                                            <Link key={index} href={book.href} className="block h-full">
                                                {CardContent}
                                            </Link>
                                        ) : (
                                            <div key={index} className="block h-full">
                                                {CardContent}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    searchQuery && (
                        <div className="text-center py-12">
                            <Icon name="MagnifyingGlassIcon" size={48} className="mx-auto text-muted-foreground mb-4 opacity-50" />
                            <h3 className="text-xl font-semibold text-foreground mb-2">No books found</h3>
                            <p className="text-muted-foreground mb-4">
                                We couldn't find any books matching "{searchQuery}"
                            </p>
                            <button
                                onClick={() => setSearchQuery('')}
                                className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium transition-colors"
                            >
                                Clear Search
                            </button>
                        </div>
                    )
                )}

                {/* Call to Action */}
                <div className="mt-12 bg-primary/10 rounded-lg p-8 text-center border border-primary/20">
                    <Icon name="LightBulbIcon" size={40} className="mx-auto text-primary mb-4" />
                    <h3 className="text-2xl font-bold text-foreground mb-2 font-heading">
                        Have a Resource to Share?
                    </h3>
                    <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                        We're building this library with contributions from our community. If you have guides, tutorials, or resources to share, we'd love to hear from you!
                    </p>
                    <Link
                        href="/contact"
                        className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium transition-colors"
                    >
                        <Icon name="PaperAirplaneIcon" size={20} className="mr-2" />
                        Contact Us
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default LibraryPage;
